import bus from '@/EventBus'
import config from '@/config'
import OrderService from '@/services/OrderService'
import LogService from '@/services/LogService'
import RouteService from '@/services/RouteService'
import LocationService from '@/services/LocationService'

const orderFromCollectionResponse = (res) => {
	const data = res.data['hydra:member'] || res.data['member'] || res.data
	return Array.isArray(data) ? data[0] : data
}

const logsFromCollectionResponse = (res) => {
	return res.data['hydra:member'] || res.data['member'] || []
}

const attachLogsToOrder = (order) => {
	if (!order || !order.id) return Promise.resolve(order)
	return LogService.getByOrderId(order.id)
		.then((logsRes) => ({ ...order, logs: logsFromCollectionResponse(logsRes) }))
		.catch(() => ({ ...order, logs: [] }))
}

const refreshOrderById = (commit, id, fallback) => {
	if (id == null) {
		commit('SET_CURRENT_ORDER', fallback)
		return Promise.resolve(fallback)
	}
	return OrderService.find(id)
		.then((findRes) => {
			const o = orderFromCollectionResponse(findRes)
			if (o && o.id) {
				return attachLogsToOrder(o).then((orderWithLogs) => {
					commit('SET_CURRENT_ORDER', orderWithLogs)
					return orderWithLogs
				})
			}
			commit('SET_CURRENT_ORDER', fallback)
			return fallback
		})
		.catch(() => {
			commit('SET_CURRENT_ORDER', fallback)
			return fallback
		})
}

// initial state
const state = {
	currentOrder: null,
	searchText: '',
	currentTab: null,
	viewMode: 'orders',
	filterCountry: null
}

// actions
const actions = {
	selectTab({ commit }, tabName) {
		commit('SET_CURRENT_TAB', tabName)
		bus.$emit('tab-change')
	},
	selectViewMode({ commit }, viewMode) {
		commit('SET_CURRENT_VIEW_MODE', viewMode)
		bus.$emit('change-view-mode')
	},
	selectFilterCountry({ commit, state }, filterCountry) {
		if (state.filterCountry === filterCountry) return
		commit('SET_CURRENT_FILTER_COUNTRY', filterCountry)
		bus.$emit('change-filter-country')
	},
	search({ commit }, text) {
		commit('SET_SEARCH_TEXT', text)
		bus.$emit('search')
	},
	setInitialOrder({ commit }, user) {
		const order = {
			isInvoice: false,
			isApproved: false,
			isUrgent: false,
			isGroundPrepared: false,
			status: 'pending',
			creator: user.id,
			broker: user.name,
			paymentVat: 'brutto 23%',
			currency: 'PLN',
			country: 'PL'
		}

		commit('SET_CURRENT_ORDER', order)
	},
	setCurrentOrder({ commit }, order) {
		if (order && order.id && (!order.logs || order.logs.length === 0)) {
			return OrderService.find(order.id).then((res) => {
				const found = orderFromCollectionResponse(res)
				return attachLogsToOrder(found).then((orderWithLogs) => {
					commit('SET_CURRENT_ORDER', orderWithLogs)
				})
			})
		}
		commit('SET_CURRENT_ORDER', order)
		return Promise.resolve()
	},
	updateOrder({ commit, state }, order) {
		commit('SET_CURRENT_ORDER', order)
		return OrderService.update(state.currentOrder).then((res) =>
			refreshOrderById(commit, res.data.id, res.data)
		)
	},
	acceptOrder({ commit, state }, order) {
		commit('SET_CURRENT_ORDER', order)
		commit('SET_ORDER_STATUS', 'new')
		return OrderService.update(state.currentOrder).then((res) =>
			refreshOrderById(commit, res.data.id, res.data)
		)
	},
	archiveOrder({ commit, state }, order) {
		commit('SET_CURRENT_ORDER', order)
		commit('SET_ORDER_STATUS', 'archived')
		return OrderService.update(state.currentOrder).then((res) =>
			refreshOrderById(commit, res.data.id, res.data)
		)
	},
	unarchiveOrderToNew({ commit, state }, order) {
		commit('SET_CURRENT_ORDER', order)
		commit('REMOVE_ROUTE_FROM_ORDER')
		commit('SET_ORDER_STATUS', 'new')
		return OrderService.update(state.currentOrder).then((res) =>
			refreshOrderById(commit, res.data.id, res.data)
		)
	},
	moveToTrash({ commit, state }, order) {
		commit('SET_CURRENT_ORDER', order)
		commit('SET_ORDER_STATUS', 'trashed')
		return OrderService.update(state.currentOrder).then((res) =>
			refreshOrderById(commit, res.data.id, res.data)
		)
	},
	moveToComplaint({ commit, state }, order) {
		commit('SET_CURRENT_ORDER', order)
		commit('SET_ORDER_STATUS', 'complaint')
		return OrderService.update(state.currentOrder).then((res) =>
			refreshOrderById(commit, res.data.id, res.data)
		)
	},
	unarchiveOrderToPending({ commit, state }, order) {
		commit('SET_CURRENT_ORDER', order)
		commit('REMOVE_ROUTE_FROM_ORDER')
		commit('SET_ORDER_STATUS', 'pending')
		return OrderService.update(state.currentOrder).then((res) =>
			refreshOrderById(commit, res.data.id, res.data)
		)
	},
	createOrder({ commit, state }, { order }) {
		commit('SET_CURRENT_ORDER', order)
		return OrderService.create(state.currentOrder).then((res) =>
			refreshOrderById(commit, res.data.id, res.data)
		)
	},
	addToExisitingRoute({ commit, state }, { order, route }) {
		commit('SET_CURRENT_ORDER', order)
		commit('ADD_ROUTE_TO_ORDER', route)
		commit('SET_ORDER_STATUS', route.status)
		return OrderService.update(state.currentOrder).then((res) =>
			refreshOrderById(commit, res.data.id, res.data)
		)
	},
	removeFromRoute({ commit, state }, order) {
		commit('SET_CURRENT_ORDER', order)
		commit('REMOVE_ROUTE_FROM_ORDER')
		commit('SET_ORDER_STATUS', 'new')
		return OrderService.update(state.currentOrder).then((res) =>
			refreshOrderById(commit, res.data.id, res.data)
		)
	},
	addToNewRoute({ commit, state }, { order, routeName }) {
		commit('SET_CURRENT_ORDER', order)
		return RouteService.create({ name: routeName, status: 'planned' }).then((res) => {
			commit('ADD_ROUTE_TO_ORDER', res.data)
			commit('SET_ORDER_STATUS', res.data.status)
			return OrderService.update(state.currentOrder).then((upd) =>
				refreshOrderById(commit, upd.data.id, upd.data)
			)
		})
	},
	deleteOrder({ commit, state }, order) {
		commit('SET_CURRENT_ORDER', order)
		return OrderService.destroy(state.currentOrder).then(() => {
			commit('SET_CURRENT_ORDER', null)
			return order
		})
	},
	findLocation({ state }) {
		return LocationService.find(state.currentOrder)
	},
	updateOrderLocation({ commit, state }, location) {
		commit('SET_ORDER_LOCATION', location)
		return OrderService.update(state.currentOrder).then((res) =>
			refreshOrderById(commit, res.data.id, res.data)
		)
	},
	clearLocation({ commit, state }) {
		commit('CLEAR_ORDER_LOCATION')
		return OrderService.update(state.currentOrder).then((res) =>
			refreshOrderById(commit, res.data.id, res.data)
		)
	},
	sendEmail(_, data) {
		return OrderService.sendEmail(data)
	}
}

// mutations
const mutations = {
	SET_CURRENT_TAB(state, tabName) {
		state.currentTab = tabName
	},
	SET_CURRENT_VIEW_MODE(state, mode) {
		state.viewMode = mode
	},
	SET_CURRENT_FILTER_COUNTRY(state, filterCountry) {
		state.filterCountry = filterCountry
	},
	SET_SEARCH_TEXT(state, text) {
		state.searchText = text
	},
	SET_CURRENT_ORDER(state, order) {
		state.currentOrder = Object.assign({}, order) // remove reference
	},
	SET_NEW_ORDER_PARAMS(state, user) {
		state.currentOrder.isInvoice = false
		state.currentOrder.isApproved = false
		state.currentOrder.isUrgent = false
		state.currentOrder.status = 'pending'
		state.currentOrder.creator = user.id
		state.currentOrder.broker = user.name
		if (!config.company.isNettoBruttoEnabled) state.currentOrder.paymentVat = 'brutto'
		if (!config.company.isMultiCurrencyEnabled) state.currentOrder.currency = 'PLN'
	},
	ADD_ROUTE_TO_ORDER(state, route) {
		state.currentOrder.route = route
	},
	REMOVE_ROUTE_FROM_ORDER(state) {
		state.currentOrder.route = null
	},
	SET_ORDER_STATUS(state, status) {
		state.currentOrder.status = status
	},
	SET_ORDER_LOCATION(state, location) {
		state.currentOrder.lat = location.lat
		state.currentOrder.lon = location.lon
	},
	CLEAR_ORDER_LOCATION(state) {
		state.currentOrder.lat = null
		state.currentOrder.lon = null
	}
}

export default {
	namespaced: true,
	state,
	actions,
	mutations
}
